import React, { useEffect, useMemo, useState } from "react";
import {
  addDoc,
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import {
  FiBriefcase,
  FiCalendar,
  FiImage,
  FiMessageCircle,
  FiMusic,
  FiPhone,
  FiPlayCircle,
  FiSave,
  FiSend,
  FiUsers,
  FiVideo,
} from "react-icons/fi";
import PermissionGuard from "../components/PermissionGuard";
import { db, storage } from "../firebase";
import { ensurePanelAdminSession } from "../utils/panelSession";

const COLORS = {
  page: "#f4f6fb",
  card: "#ffffff",
  border: "#e2e8f0",
  text: "#0f172a",
  subtext: "#475569",
  primary: "#f20c53",
  secondary: "#fb6c33",
  ok: "#166534",
  okBg: "#ecfdf5",
  err: "#b91c1c",
  errBg: "#fff1f2",
};

const OFFICIAL_PROFILE_NAME = "ApnaArtist Official";
const OFFICIAL_USERNAME = "apnaartist";

const CREATE_OPTIONS = [
  { value: "post", label: "Post", icon: <FiImage /> },
  { value: "reel", label: "Reel", icon: <FiPlayCircle /> },
  { value: "story", label: "Story", icon: <FiVideo /> },
  { value: "opportunity", label: "Opportunity", icon: <FiBriefcase /> },
];

const BOOK_OPTIONS = [
  { value: "artist_booking", label: "Book Artist", icon: <FiUsers /> },
  { value: "mentor_video", label: "Book Mentor Video", icon: <FiVideo /> },
  { value: "mentor_chat", label: "Book Mentor Chat", icon: <FiMessageCircle /> },
  { value: "mentor_call", label: "Book Mentor Call", icon: <FiPhone /> },
  { value: "song_request", label: "Song Request", icon: <FiMusic /> },
  { value: "announcement", label: "Announcement", icon: <FiSend /> },
  { value: "event_booking", label: "Event Booking", icon: <FiCalendar /> },
];

const DEFAULT_PROFILE = () => ({
  phone: "",
  name: OFFICIAL_PROFILE_NAME,
  username: OFFICIAL_USERNAME,
  city: "Mumbai",
  email: "support@apnaartist.in",
  bio: "Official ApnaArtist profile managed from the admin panel.",
  imageUrl: buildBrandAssetUrl(),
});

const DEFAULT_CREATE_FORM = {
  caption: "",
  mediaUrl: "",
  title: "",
  location: "",
  feeling: "",
  emoji: "",
  taggedPeople: "",
  backgroundColor: "",
  city: "",
  category: "",
  requirementType: "artist",
  description: "",
  budgetMin: "",
  budgetMax: "",
  neededDate: "",
  skills: "",
  isFeatured: false,
};

const DEFAULT_BOOK_FORM = {
  artistId: "",
  mentorId: "",
  eventId: "",
  ticketId: "",
  quantity: "1",
  contactName: "",
  contactPhone: "",
  contactEmail: "",
  eventType: "",
  eventAddress: "",
  dateFrom: "",
  dateTo: "",
  timeFrom: "",
  timeTo: "",
  subtotal: "",
  gst: "",
  total: "",
  specialNote: "",
  occasion: "",
  venueName: "",
  address: "",
  city: "",
  note: "",
  amount: "",
  trackName: "",
  artistName: "",
};

export default function PostInApp() {
  const [users, setUsers] = useState([]);
  const [artists, setArtists] = useState([]);
  const [mentors, setMentors] = useState([]);
  const [events, setEvents] = useState([]);
  const [tab, setTab] = useState("create");
  const [createType, setCreateType] = useState("post");
  const [bookType, setBookType] = useState("artist_booking");
  const [existingUserPhone, setExistingUserPhone] = useState("");
  const [profileEditorOpen, setProfileEditorOpen] = useState(false);
  const [profileForm, setProfileForm] = useState(DEFAULT_PROFILE);
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [activeProfile, setActiveProfile] = useState(null);
  const [createForm, setCreateForm] = useState(DEFAULT_CREATE_FORM);
  const [createMediaFile, setCreateMediaFile] = useState(null);
  const [bookForm, setBookForm] = useState(DEFAULT_BOOK_FORM);
  const [busyKey, setBusyKey] = useState("");
  const [popup, setPopup] = useState({ open: false, type: "ok", title: "", message: "" });
  const [createStatus, setCreateStatus] = useState({ type: "", message: "" });

  useEffect(() => {
    const unsubUsers = onSnapshot(collection(db, "users"), (snap) => {
      const rows = snap.docs
        .map((entry) => {
          const data = entry.data() || {};
          return normalizeUser(entry.id, data);
        })
        .sort((left, right) => String(left.name || "").localeCompare(String(right.name || "")));
      setUsers(rows);
    });

    const unsubArtists = onSnapshot(collection(db, "artists"), (snap) => {
      const rows = snap.docs
        .map((entry) => {
          const data = entry.data() || {};
          return {
            id: entry.id,
            uid: String(data.uid || entry.id || ""),
            name: String(data.stageName || data.stage_name || data.artistName || data.name || "Artist"),
            phone: digits(data.phone || data.mobile || data.userPhone || ""),
            imageUrl: String(data.profileImage || data.profileUrl || data.imageUrl || data.avatar || ""),
          };
        })
        .sort((left, right) => left.name.localeCompare(right.name));
      setArtists(rows);
    });

    const unsubMentors = onSnapshot(collection(db, "mentors"), (snap) => {
      const rows = snap.docs
        .map((entry) => {
          const data = entry.data() || {};
          return {
            id: entry.id,
            name: String(data.name || data.fullName || data.stageName || "Mentor"),
            phone: digits(data.phone || data.mobile || data.userPhone || ""),
            imageUrl: String(data.profileImage || data.profileUrl || data.imageUrl || data.avatar || ""),
          };
        })
        .sort((left, right) => left.name.localeCompare(right.name));
      setMentors(rows);
    });

    const unsubEvents = onSnapshot(query(collection(db, "create_new_event"), orderBy("createdAt", "desc")), (snap) => {
      const rows = snap.docs.map((entry) => {
        const data = entry.data() || {};
        const tickets = Array.isArray(data.tickets) ? data.tickets : [];
        return {
          id: entry.id,
          name: String(data.name || data.eventName || "Untitled Event"),
          imageUrl: String(
            data?.images?.horizontalBanner?.url ||
              data?.images?.verticalPoster?.url ||
              data?.imageUrl ||
              ""
          ),
          venueName: String(data?.schedule?.venueDetails?.venueName || data?.schedule?.venueName || ""),
          date: String(firstShowDate(data?.schedule?.shows) || ""),
          time: String(firstShowTime(data?.schedule?.shows) || ""),
          tickets: tickets
            .filter((ticket) => ticket && ticket.active !== false)
            .map((ticket) => ({
              id: String(ticket.id || ticket.ticket_id || ticket.name || `ticket_${Math.random()}`),
              name: String(ticket.name || "General"),
              price: Number(ticket.price || 0),
              qty: Number(ticket.qty || 0),
            })),
        };
      });
      setEvents(rows);
    });

    return () => {
      unsubUsers();
      unsubArtists();
      unsubMentors();
      unsubEvents();
    };
  }, []);

  useEffect(() => {
    if (!existingUserPhone) return;
    const found = users.find((user) => user.phone === existingUserPhone);
    if (!found) return;
    setProfileForm({
      phone: found.phone,
      name: found.name,
      username: found.username,
      city: found.city,
      email: found.email,
      bio: found.bio,
      imageUrl: found.imageUrl || buildBrandAssetUrl(),
    });
    setActiveProfile(found);
    setProfileEditorOpen(false);
  }, [existingUserPhone, users]);

  const selectedArtist = useMemo(
    () => artists.find((artist) => artist.id === bookForm.artistId) || null,
    [artists, bookForm.artistId]
  );
  const selectedMentor = useMemo(
    () => mentors.find((mentor) => mentor.id === bookForm.mentorId) || null,
    [mentors, bookForm.mentorId]
  );
  const selectedEvent = useMemo(
    () => events.find((event) => event.id === bookForm.eventId) || null,
    [events, bookForm.eventId]
  );
  const selectedTicket = useMemo(
    () => (selectedEvent?.tickets || []).find((ticket) => ticket.id === bookForm.ticketId) || null,
    [selectedEvent, bookForm.ticketId]
  );

  const canSubmitCreate = Boolean(activeProfile?.phone);
  const canSubmitBook = Boolean(activeProfile?.phone);

  function openPopup(type, title, message) {
    setPopup({ open: true, type, title, message });
  }

  function closePopup() {
    setPopup({ open: false, type: "ok", title: "", message: "" });
  }

  async function saveProfile() {
    setBusyKey("profile");
    try {
      await ensurePanelAdminSession({ forceRefresh: true });

      const phone = digits(profileForm.phone);
      if (phone.length < 10) {
        throw new Error("Enter a valid app user phone number.");
      }

      const name = cleanText(profileForm.name);
      if (!name) {
        throw new Error("Enter profile name.");
      }

      const imageUpload = await resolveAsset({
        file: profileImageFile,
        directUrl: profileForm.imageUrl,
        folder: `admin_app_profiles/${phone}`,
        filenamePrefix: "profile",
      });
      const isOfficial = isOfficialProfile({ phone, name, username: profileForm.username });

      const payload = {
        uid: phone,
        phone,
        mobile: phone,
        name,
        fullName: name,
        displayName: name,
        username: sanitizeUsername(profileForm.username || name),
        city: cleanText(profileForm.city),
        email: cleanText(profileForm.email),
        bio: cleanText(profileForm.bio),
        role: "user",
        isOfficialApnaArtist: isOfficial,
        notificationBroadcastMode: isOfficial ? "all" : "",
        profileImage: imageUpload.url,
        profile_image_url: imageUpload.url,
        imageUrl: imageUpload.url,
        avatar: imageUpload.url,
        profilePic: imageUpload.url,
        photoURL: imageUpload.url,
        updatedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
      };

      if (imageUpload.storagePath) {
        payload.profileImagePath = imageUpload.storagePath;
      }

      await setDoc(doc(db, "users", phone), payload, { merge: true });

      const nextProfile = normalizeUser(phone, payload);
      setActiveProfile(nextProfile);
      setExistingUserPhone(phone);
      setProfileImageFile(null);
      openPopup("ok", "App Profile Saved", `${name} is ready for admin-created app content.`);
    } catch (error) {
      console.error("saveProfile failed", error);
      openPopup("err", "Profile Save Failed", error?.message || "Unable to save app profile.");
    } finally {
      setBusyKey("");
    }
  }

  function useOfficialPreset() {
    const next = DEFAULT_PROFILE();
    setProfileForm(next);
    setProfileImageFile(null);
    setProfileEditorOpen(true);
  }

  async function submitCreate() {
    setBusyKey("create");
    setCreateStatus({ type: "progress", message: "Checking admin session..." });
    try {
      await withTimeout(ensurePanelAdminSession(), 15000, "Admin session check took too long. Please try again.");
      if (!activeProfile?.phone) {
        throw new Error("Create or select an app user profile first.");
      }

      setCreateStatus({ type: "progress", message: `Creating ${labelForCreate(createType)}...` });
      if (createType === "opportunity") {
        await createOpportunity();
      } else if (createType === "story") {
        await createStory();
      } else {
        await createPostLike();
      }

      setCreateForm(DEFAULT_CREATE_FORM);
      setCreateMediaFile(null);
      setCreateStatus({ type: "ok", message: `${labelForCreate(createType)} created successfully.` });
      openPopup("ok", "Created In App", `${labelForCreate(createType)} saved into the live app database.`);
    } catch (error) {
      console.error("submitCreate failed", error);
      setCreateStatus({ type: "err", message: error?.message || "Unable to create in app." });
      openPopup("err", "Create Failed", error?.message || "Unable to create in app.");
    } finally {
      setBusyKey("");
    }
  }

  async function submitBook() {
    setBusyKey("book");
    try {
      await ensurePanelAdminSession();
      if (!activeProfile?.phone) {
        throw new Error("Create or select an app user profile first.");
      }

      if (bookType === "artist_booking") {
        await createArtistBooking();
      } else if (bookType === "mentor_video" || bookType === "mentor_chat" || bookType === "mentor_call") {
        await createMentorBooking();
      } else if (bookType === "event_booking") {
        await createEventBooking();
      } else {
        await createSongOrAnnouncement();
      }

      setBookForm(DEFAULT_BOOK_FORM);
      openPopup("ok", "Booked In App", `${labelForBook(bookType)} saved into the live app database.`);
    } catch (error) {
      console.error("submitBook failed", error);
      openPopup("err", "Booking Failed", error?.message || "Unable to save booking/request.");
    } finally {
      setBusyKey("");
    }
  }

async function createPostLike() {
    const itemId = String(Date.now());
    const isReel = createType === "reel";
    const rootCollection = isReel ? "users_reel" : "users_post";
    const itemCollection = isReel ? "reels" : "posts";
    const media = await resolveAsset({
      file: createMediaFile,
      directUrl: createForm.mediaUrl,
      folder: `${rootCollection}/${activeProfile.phone}/${itemCollection}`,
      filenamePrefix: itemId,
    });
    if (!media.url) {
      throw new Error(`Upload or paste a media URL for the ${createType}.`);
    }

    const mediaType = inferMediaType(media.url);
    const isOfficial = isOfficialProfile(activeProfile);
    const mediaImage = mediaType === "image" ? media.url : "";
    const mediaVideo = mediaType === "video" ? media.url : "";
    const payload = {
      ...(isReel ? { reelId: itemId } : { postId: itemId }),
      ownerId: activeProfile.phone,
      ownerType: "user",
      author: activeProfile.name,
      authorName: activeProfile.name,
      userName: activeProfile.name,
      userImage: activeProfile.imageUrl || "",
      imageUrl: mediaImage,
      videoUrl: mediaVideo,
      fileUrl: mediaType === "file" ? media.url : "",
      media: media.url,
      mediaUrl: media.url,
      mediaType: mediaType === "video" ? "video" : "image",
      thumbnailUrl: mediaImage,
      thumbUrl: mediaImage,
      mediaThumb: mediaImage,
      storagePath: media.storagePath,
      caption: cleanText(createForm.caption),
      text: cleanText(createForm.caption),
      location: cleanText(createForm.location),
      feeling: cleanText(createForm.feeling),
      emoji: cleanText(createForm.emoji),
      taggedPeople: splitLines(createForm.taggedPeople),
      backgroundColor: parseBackgroundColor(createForm.backgroundColor),
      postType: createType,
      contentType: createType,
      isReel,
      isArtist: false,
      isOfficialApnaArtist: isOfficial,
      adminBroadcastToAll: isOfficial,
      isFeatured: Boolean(createForm.isFeatured),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      likesCount: 0,
      commentsCount: 0,
      viewsCount: 0,
    };

    await setDoc(
      doc(db, rootCollection, activeProfile.phone),
      {
        ownerId: activeProfile.phone,
        ownerType: "user",
        author: activeProfile.name,
        userName: activeProfile.name,
        userImage: activeProfile.imageUrl || "",
        imageUrl: activeProfile.imageUrl || "",
        updatedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
      },
      { merge: true }
    );

    await setDoc(doc(db, rootCollection, activeProfile.phone, itemCollection, itemId), payload);
  }

  async function createStory() {
    const storyId = String(Date.now());
    const media = await resolveAsset({
      file: createMediaFile,
      directUrl: createForm.mediaUrl,
      folder: `users_story/${activeProfile.phone}`,
      filenamePrefix: storyId,
    });
    if (!media.url) {
      throw new Error("Upload or paste a story media URL.");
    }

    await setDoc(
      doc(db, "latest_stories", activeProfile.phone),
      {
        ownerId: activeProfile.phone,
        ownerType: "user",
        author: activeProfile.name,
        imageUrl: activeProfile.imageUrl || "",
        updatedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
      },
      { merge: true }
    );

    const mediaType = inferMediaType(media.url);
    const isOfficial = isOfficialProfile(activeProfile);
    const storyPayload = {
      ownerId: activeProfile.phone,
      ownerType: "user",
      role: "user",
      isArtist: false,
      storyId,
      title: cleanText(createForm.title) || "ApnaArtist Story",
      author: activeProfile.name,
      authorName: activeProfile.name,
      name: activeProfile.name,
      userName: activeProfile.name,
      userImage: activeProfile.imageUrl || "",
      media: media.url,
      mediaUrl: media.url,
      imageUrl: mediaType === "image" ? media.url : "",
      videoUrl: mediaType === "video" ? media.url : "",
      mediaType: mediaType === "video" ? "video" : "image",
      thumbnailUrl: mediaType === "image" ? media.url : "",
      thumbUrl: mediaType === "image" ? media.url : "",
      fileUrl: mediaType === "file" ? media.url : "",
      storagePath: media.storagePath,
      location: cleanText(createForm.location),
      summary: cleanText(createForm.caption),
      caption: cleanText(createForm.caption),
      text: cleanText(createForm.caption),
      isOfficialApnaArtist: isOfficial,
      adminBroadcastToAll: isOfficial,
      views: 0,
      viewCount: 0,
      seen: 0,
      createdAt: serverTimestamp(),
      expiresAt: Timestamp.fromMillis(Date.now() + 24 * 60 * 60 * 1000),
    };

    await setDoc(doc(db, "users_story", activeProfile.phone), {
      ownerId: activeProfile.phone,
      ownerType: "user",
      updatedAt: serverTimestamp(),
      createdAt: serverTimestamp(),
    }, { merge: true });

    await addDoc(collection(db, "users_story", activeProfile.phone, "stories"), storyPayload);
    await addDoc(collection(db, "latest_stories", activeProfile.phone, "stories"), storyPayload);
  }

  async function createOpportunity() {
    const title = cleanText(createForm.title);
    const description = cleanText(createForm.description);
    const city = cleanText(createForm.city);
    if (!title || !description || !city) {
      throw new Error("Opportunity title, description, and city are required.");
    }

    const isOfficial = isOfficialProfile(activeProfile);
    await addDoc(collection(db, "new_oppurtunity"), {
      title,
      projectName: title,
      description,
      city,
      location: city,
      venueName: city,
      category: cleanText(createForm.category) || cleanText(createForm.requirementType) || "artist",
      requirementType: cleanText(createForm.requirementType) || "artist",
      organizerName: activeProfile.name,
      organizerId: activeProfile.phone,
      createdByName: activeProfile.name,
      createdByFullName: activeProfile.name,
      createdByPhone: activeProfile.phone,
      createdByUid: activeProfile.phone,
      createdByImage: activeProfile.imageUrl || "",
      isOfficialApnaArtist: isOfficial,
      adminBroadcastToAll: isOfficial,
      budgetMin: toNumber(createForm.budgetMin),
      budgetMax: toNumber(createForm.budgetMax),
      budgetFrom: toNumber(createForm.budgetMin),
      budgetTo: toNumber(createForm.budgetMax),
      duration: createForm.neededDate ? "Scheduled" : "",
      eventDate: createForm.neededDate ? Timestamp.fromDate(new Date(createForm.neededDate)) : null,
      neededDate: createForm.neededDate ? Timestamp.fromDate(new Date(createForm.neededDate)) : null,
      requiredSkills: splitLines(createForm.skills),
      skills: splitLines(createForm.skills),
      status: "open",
      approvalStatus: "approved",
      isFeatured: Boolean(createForm.isFeatured),
      paymentStatus: "pending",
      taskStatus: "not_started",
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  }

  async function createArtistBooking() {
    if (!selectedArtist?.id) {
      throw new Error("Choose an artist to book.");
    }

    const contactName = cleanText(bookForm.contactName) || activeProfile.name;
    const contactPhone = digits(bookForm.contactPhone) || activeProfile.phone;
    if (!contactName || !contactPhone) {
      throw new Error("Contact name and phone are required for artist booking.");
    }

    await addDoc(collection(db, "artist_new_bookings"), {
      bookedToStageName: selectedArtist.name,
      bookedToArtistUid: selectedArtist.uid || selectedArtist.id,
      bookedToPhone: selectedArtist.phone || "",
      bookedByName: activeProfile.name,
      bookedByUsername: activeProfile.username || sanitizeUsername(activeProfile.name),
      bookedByPhone: activeProfile.phone,
      bookedByUid: activeProfile.phone,
      contactName,
      contactPhone,
      eventType: cleanText(bookForm.eventType) || "Performance Booking",
      eventAddress: cleanText(bookForm.eventAddress),
      dateFrom: cleanText(bookForm.dateFrom),
      dateTo: cleanText(bookForm.dateTo) || cleanText(bookForm.dateFrom),
      timeFrom: cleanText(bookForm.timeFrom),
      timeTo: cleanText(bookForm.timeTo),
      subtotal: toNumber(bookForm.subtotal),
      gst: toNumber(bookForm.gst),
      total: toNumber(bookForm.total || bookForm.subtotal),
      paymentId: "",
      paymentStatus: "pending",
      paymentSuccess: false,
      partialPaymentStatus: "",
      specialNote: cleanText(bookForm.specialNote),
      status: "pending",
      imageUrl: activeProfile.imageUrl || selectedArtist.imageUrl || "",
      eventStarted: false,
      eventCompleted: false,
      createdAt: serverTimestamp(),
    });
  }

  async function createMentorBooking() {
    if (!selectedMentor?.id) {
      throw new Error("Choose a mentor to book.");
    }

    const collectionName =
      bookType === "mentor_video"
        ? "video_request_mentor"
        : bookType === "mentor_chat"
        ? "chat_request_mentor"
        : "call_request_mentor";

    await addDoc(collection(db, collectionName), {
      mode:
        bookType === "mentor_video"
          ? "video"
          : bookType === "mentor_chat"
          ? "chat"
          : "call",
      userName: activeProfile.name,
      userPhone: activeProfile.phone,
      mentorId: selectedMentor.id,
      mentorPhone: selectedMentor.phone || "",
      date: cleanText(bookForm.dateFrom),
      fromTime: cleanText(bookForm.timeFrom),
      toTime: cleanText(bookForm.timeTo),
      preferredStartTime: cleanText(bookForm.timeFrom),
      preferredEndTime: cleanText(bookForm.timeTo),
      amountPaid: toNumber(bookForm.amount),
      totalPrice: toNumber(bookForm.amount),
      channelName: cleanText(bookForm.specialNote),
      status: "pending",
      imageUrl: activeProfile.imageUrl || selectedMentor.imageUrl || "",
      createdAt: serverTimestamp(),
    });
  }

  async function createSongOrAnnouncement() {
    const isAnnouncement = bookType === "announcement";
    if (!isAnnouncement && !cleanText(bookForm.trackName)) {
      throw new Error("Track name is required for song request.");
    }

    await addDoc(collection(db, "new_song_announcement"), {
      type: isAnnouncement ? "announcement" : "song",
      stageName: activeProfile.name,
      userPhone: activeProfile.phone,
      requesterPhone: activeProfile.phone,
      createdByPhone: activeProfile.phone,
      phone: activeProfile.phone,
      trackName: cleanText(bookForm.trackName),
      artistName: cleanText(bookForm.artistName),
      spotify: {
        track_name: cleanText(bookForm.trackName),
        artist_name: cleanText(bookForm.artistName),
      },
      occasion: cleanText(bookForm.occasion),
      venueName: cleanText(bookForm.venueName),
      address: cleanText(bookForm.address),
      city: cleanText(bookForm.city),
      date: cleanText(bookForm.dateFrom),
      timeFrom: cleanText(bookForm.timeFrom),
      timeTo: cleanText(bookForm.timeTo),
      note: cleanText(bookForm.note),
      announcement_text: isAnnouncement ? cleanText(bookForm.note) : "",
      bid: toNumber(bookForm.amount),
      payment: {
        grand_total: toNumber(bookForm.amount),
        currency: "INR",
      },
      status: "pending",
      isFeatured: false,
      imageUrl: activeProfile.imageUrl || "",
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  }

  async function createEventBooking() {
    if (!selectedEvent?.id || !selectedTicket?.id) {
      throw new Error("Choose an event and a ticket.");
    }

    const quantity = Math.max(1, Math.floor(toNumber(bookForm.quantity) || 1));
    const subtotal = quantity * Number(selectedTicket.price || 0);
    const totalAmount = subtotal;
    const contactName = cleanText(bookForm.contactName) || activeProfile.name;
    const contactPhone = digits(bookForm.contactPhone) || activeProfile.phone;
    const contactEmail = cleanText(bookForm.contactEmail || activeProfile.email);
    if (!contactName || !contactPhone || !contactEmail) {
      throw new Error("Event booking needs contact name, phone, and email.");
    }

    await addDoc(collection(db, "new_event_booking_recived"), {
      eventId: selectedEvent.id,
      eventTitle: selectedEvent.name,
      eventName: selectedEvent.name,
      eventDate: selectedEvent.date || "",
      eventTime: selectedEvent.time || "",
      eventImage: selectedEvent.imageUrl || "",
      userName: activeProfile.name,
      userPhone: activeProfile.phone,
      name: activeProfile.name,
      contact: {
        name: contactName,
        phone: contactPhone,
        email: contactEmail,
      },
      items: [
        {
          ticket_id: selectedTicket.id,
          tier_name: selectedTicket.name,
          quantity,
          unit_price: Number(selectedTicket.price || 0),
          total: totalAmount,
        },
      ],
      venue: {
        venueName: selectedEvent.venueName || "",
      },
      venueName: selectedEvent.venueName || "",
      summary: {
        subtotal,
        total_amount: totalAmount,
        gst_amount: 0,
        convenience_fee: 0,
        platform_fee: 0,
      },
      terms: {
        accepted: true,
      },
      status: "confirmed",
      paymentStatus: "manual_admin",
      paymentProvider: "admin_panel",
      imageUrl: activeProfile.imageUrl || "",
      createdAt: serverTimestamp(),
      created_at: serverTimestamp(),
    });
  }

  return (
    <PermissionGuard
      requiredPermission={[
        "view_posts",
        "view_story",
        "view_opportunities",
        "view_artist_bookings",
        "view_mentor_bookings",
        "view_song_request",
        "view_booked_events",
      ]}
    >
      <div className="aa-pro-page" style={page}>
        <div className="aa-pro-hero" style={hero}>
          <div style={heroHeading}>
            <h1 style={heroTitle}>Apnaartist Official</h1>
          </div>
        </div>

        <div style={layoutGrid}>
          <section style={panelCard}>
            <div style={sectionHeader}>
              <div>
                <div style={sectionTitle}>App User Profile</div>
                <div style={sectionCopy}>
                  Create or load the app-side user profile that admin content should post from.
                </div>
              </div>
              <div style={buttonRow}>
                <button type="button" style={ghostBtn} onClick={useOfficialPreset}>
                  Create New Profile
                </button>
                {activeProfile?.phone ? (
                  <button
                    type="button"
                    style={ghostBtn}
                    onClick={() => {
                      setProfileForm({
                        phone: activeProfile.phone || "",
                        name: activeProfile.name || OFFICIAL_PROFILE_NAME,
                        username: activeProfile.username || OFFICIAL_USERNAME,
                        city: activeProfile.city || "",
                        email: activeProfile.email || "",
                        bio: activeProfile.bio || "",
                        imageUrl: activeProfile.imageUrl || buildBrandAssetUrl(),
                      });
                      setProfileImageFile(null);
                      setProfileEditorOpen(true);
                    }}
                  >
                    Edit Profile
                  </button>
                ) : null}
              </div>
            </div>

            <label style={label}>
              Channel / user
              <select
                value={existingUserPhone}
                onChange={(event) => setExistingUserPhone(event.target.value)}
                style={input}
              >
                <option value="">Select existing user</option>
                {users.map((user) => (
                  <option key={user.phone} value={user.phone}>
                    {user.name} | {user.phone}
                  </option>
                ))}
              </select>
            </label>

            {profileEditorOpen ? (
              <>
                <div style={fieldGrid}>
                  <label style={label}>
                    Phone
                    <input
                      value={profileForm.phone}
                      onChange={(event) => setProfileForm((prev) => ({ ...prev, phone: digits(event.target.value) }))}
                      style={input}
                      placeholder="User phone number"
                    />
                  </label>

                  <label style={label}>
                    Name
                    <input
                      value={profileForm.name}
                      onChange={(event) => setProfileForm((prev) => ({ ...prev, name: event.target.value }))}
                      style={input}
                      placeholder="Profile display name"
                    />
                  </label>

                  <label style={label}>
                    Username
                    <input
                      value={profileForm.username}
                      onChange={(event) => setProfileForm((prev) => ({ ...prev, username: event.target.value }))}
                      style={input}
                      placeholder="username"
                    />
                  </label>

                  <label style={label}>
                    City
                    <input
                      value={profileForm.city}
                      onChange={(event) => setProfileForm((prev) => ({ ...prev, city: event.target.value }))}
                      style={input}
                      placeholder="City"
                    />
                  </label>

                  <label style={label}>
                    Email
                    <input
                      value={profileForm.email}
                      onChange={(event) => setProfileForm((prev) => ({ ...prev, email: event.target.value }))}
                      style={input}
                      placeholder="Email"
                    />
                  </label>

                  <label style={label}>
                    Profile image URL
                    <input
                      value={profileForm.imageUrl}
                      onChange={(event) => setProfileForm((prev) => ({ ...prev, imageUrl: event.target.value }))}
                      style={input}
                      placeholder="Public image URL"
                    />
                  </label>
                </div>

                <label style={label}>
                  Profile bio
                  <textarea
                    value={profileForm.bio}
                    onChange={(event) => setProfileForm((prev) => ({ ...prev, bio: event.target.value }))}
                    style={textarea}
                    placeholder="Short profile bio"
                  />
                </label>

                <label style={label}>
                  Optional local profile image upload
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(event) => setProfileImageFile(event.target.files?.[0] || null)}
                    style={input}
                  />
                </label>

                <div style={buttonRow}>
                  <button type="button" style={primaryBtn} onClick={saveProfile} disabled={busyKey === "profile"}>
                    <FiSave />
                    {busyKey === "profile" ? "Saving..." : "Save App User"}
                  </button>
                  <button
                    type="button"
                    style={ghostBtn}
                    onClick={() => setProfileEditorOpen(false)}
                    disabled={busyKey === "profile"}
                  >
                    Cancel
                  </button>
                </div>
              </>
            ) : null}

            <div style={profilePreview}>
              <div style={avatarWrap}>
                {resolveAvatar(activeProfile?.imageUrl || profileForm.imageUrl) ? (
                  <img
                    src={resolveAvatar(activeProfile?.imageUrl || profileForm.imageUrl)}
                    alt={activeProfile?.name || profileForm.name}
                    style={avatarImage}
                  />
                ) : (
                  <span>{initials(activeProfile?.name || profileForm.name)}</span>
                )}
              </div>
              <div>
                <div style={previewTitle}>{activeProfile?.name || profileForm.name || "No active profile yet"}</div>
                <div style={previewCopy}>
                  {activeProfile?.phone || profileForm.phone || "Phone required"} {activeProfile?.username ? `| @${activeProfile.username}` : ""}
                </div>
                <div style={previewCopy}>{activeProfile?.city || profileForm.city || "City not set"}</div>
              </div>
            </div>
          </section>

          <section style={workspaceCard}>
            <div style={tabRow}>
              <button
                type="button"
                onClick={() => setTab("create")}
                style={{ ...tabBtn, ...(tab === "create" ? tabBtnActive : null) }}
              >
                Create
              </button>
              <button
                type="button"
                onClick={() => setTab("book")}
                style={{ ...tabBtn, ...(tab === "book" ? tabBtnActive : null) }}
              >
                Book
              </button>
            </div>

            {tab === "create" ? (
              <>
                <TypeSelector
                  options={CREATE_OPTIONS}
                  value={createType}
                  onChange={setCreateType}
                />

                {createType === "opportunity" ? (
                  <>
                    <div style={fieldGrid}>
                      <InputField
                        label="Opportunity title"
                        value={createForm.title}
                        onChange={(value) => patchCreateForm(setCreateForm, "title", value)}
                      />
                      <InputField
                        label="City"
                        value={createForm.city}
                        onChange={(value) => patchCreateForm(setCreateForm, "city", value)}
                      />
                      <InputField
                        label="Category"
                        value={createForm.category}
                        onChange={(value) => patchCreateForm(setCreateForm, "category", value)}
                      />
                      <SelectField
                        label="Requirement type"
                        value={createForm.requirementType}
                        onChange={(value) => patchCreateForm(setCreateForm, "requirementType", value)}
                        options={[
                          { value: "artist", label: "Artist" },
                          { value: "mentor", label: "Mentor" },
                          { value: "host", label: "Host" },
                          { value: "performer", label: "Performer" },
                        ]}
                      />
                      <InputField
                        label="Budget min"
                        value={createForm.budgetMin}
                        onChange={(value) => patchCreateForm(setCreateForm, "budgetMin", value)}
                      />
                      <InputField
                        label="Budget max"
                        value={createForm.budgetMax}
                        onChange={(value) => patchCreateForm(setCreateForm, "budgetMax", value)}
                      />
                      <InputField
                        label="Needed date"
                        type="date"
                        value={createForm.neededDate}
                        onChange={(value) => patchCreateForm(setCreateForm, "neededDate", value)}
                      />
                      <InputField
                        label="Skills"
                        value={createForm.skills}
                        onChange={(value) => patchCreateForm(setCreateForm, "skills", value)}
                        placeholder="Singer, Guitar, Host"
                      />
                    </div>
                    <TextAreaField
                      label="Description"
                      value={createForm.description}
                      onChange={(value) => patchCreateForm(setCreateForm, "description", value)}
                      placeholder="Describe the opportunity"
                    />
                  </>
                ) : (
                  <>
                    <div style={fieldGrid}>
                      {createType === "story" ? (
                        <InputField
                          label="Story title"
                          value={createForm.title}
                          onChange={(value) => patchCreateForm(setCreateForm, "title", value)}
                          placeholder="Story title"
                        />
                      ) : null}
                      {createType === "post" ? (
                        <>
                          <InputField
                            label="Feeling"
                            value={createForm.feeling}
                            onChange={(value) => patchCreateForm(setCreateForm, "feeling", value)}
                            placeholder="Happy / Excited / Inspired"
                          />
                          <InputField
                            label="Location"
                            value={createForm.location}
                            onChange={(value) => patchCreateForm(setCreateForm, "location", value)}
                            placeholder="Mumbai"
                          />
                          <InputField
                            label="Emoji"
                            value={createForm.emoji}
                            onChange={(value) => patchCreateForm(setCreateForm, "emoji", value)}
                            placeholder="🔥"
                          />
                          <InputField
                            label="Tagged people"
                            value={createForm.taggedPeople}
                            onChange={(value) => patchCreateForm(setCreateForm, "taggedPeople", value)}
                            placeholder="name1, name2"
                          />
                          <InputField
                            label="Background color"
                            value={createForm.backgroundColor}
                            onChange={(value) => patchCreateForm(setCreateForm, "backgroundColor", value)}
                            placeholder="#FCE7F3"
                          />
                        </>
                      ) : null}
                      {createType === "story" ? (
                        <InputField
                          label="Location"
                          value={createForm.location}
                          onChange={(value) => patchCreateForm(setCreateForm, "location", value)}
                          placeholder="Mumbai"
                        />
                      ) : null}
                      <InputField
                        label="Media URL"
                        value={createForm.mediaUrl}
                        onChange={(value) => patchCreateForm(setCreateForm, "mediaUrl", value)}
                        placeholder="Public media URL"
                      />
                      <label style={label}>
                        Upload media
                        <input
                          type="file"
                          accept={createType === "reel" ? "video/*" : "*/*"}
                          onChange={(event) => setCreateMediaFile(event.target.files?.[0] || null)}
                          style={input}
                        />
                      </label>
                    </div>
                    <TextAreaField
                      label={createType === "story" ? "Story text" : createType === "reel" ? "Reel caption" : "Post caption"}
                      value={createForm.caption}
                      onChange={(value) => patchCreateForm(setCreateForm, "caption", value)}
                      placeholder="Write copy that should appear in the app"
                    />
                  </>
                )}

                <label style={checkLine}>
                  <input
                    type="checkbox"
                    checked={createForm.isFeatured}
                    onChange={(event) => patchCreateForm(setCreateForm, "isFeatured", event.target.checked)}
                  />
                  <span>Mark as featured where supported</span>
                </label>

                <div style={buttonRow}>
                  <button
                    type="button"
                    style={primaryBtn}
                    onClick={submitCreate}
                    disabled={busyKey === "create"}
                  >
                    <FiSend />
                    {busyKey === "create" ? "Creating..." : `Create ${labelForCreate(createType)}`}
                  </button>
                </div>
                {!canSubmitCreate ? (
                  <div style={{ ...inlineStatus, ...inlineStatusErr }}>
                    Save or select the official app profile first, then create the post.
                  </div>
                ) : null}
                {createStatus.message ? (
                  <div
                    style={{
                      ...inlineStatus,
                      ...(createStatus.type === "err"
                        ? inlineStatusErr
                        : createStatus.type === "ok"
                        ? inlineStatusOk
                        : inlineStatusProgress),
                    }}
                  >
                    {createStatus.message}
                  </div>
                ) : null}
              </>
            ) : (
              <>
                <TypeSelector
                  options={BOOK_OPTIONS}
                  value={bookType}
                  onChange={setBookType}
                />

                {bookType === "artist_booking" ? (
                  <>
                    <SelectField
                      label="Artist"
                      value={bookForm.artistId}
                      onChange={(value) => patchBookForm(setBookForm, "artistId", value)}
                      options={[
                        { value: "", label: "Select artist" },
                        ...artists.map((artist) => ({
                          value: artist.id,
                          label: `${artist.name}${artist.phone ? ` | ${artist.phone}` : ""}`,
                        })),
                      ]}
                    />
                    <div style={fieldGrid}>
                      <InputField
                        label="Contact name"
                        value={bookForm.contactName}
                        onChange={(value) => patchBookForm(setBookForm, "contactName", value)}
                      />
                      <InputField
                        label="Contact phone"
                        value={bookForm.contactPhone}
                        onChange={(value) => patchBookForm(setBookForm, "contactPhone", digits(value))}
                      />
                      <InputField
                        label="Event type"
                        value={bookForm.eventType}
                        onChange={(value) => patchBookForm(setBookForm, "eventType", value)}
                      />
                      <InputField
                        label="Date from"
                        type="date"
                        value={bookForm.dateFrom}
                        onChange={(value) => patchBookForm(setBookForm, "dateFrom", value)}
                      />
                      <InputField
                        label="Date to"
                        type="date"
                        value={bookForm.dateTo}
                        onChange={(value) => patchBookForm(setBookForm, "dateTo", value)}
                      />
                      <InputField
                        label="Time from"
                        type="time"
                        value={bookForm.timeFrom}
                        onChange={(value) => patchBookForm(setBookForm, "timeFrom", value)}
                      />
                      <InputField
                        label="Time to"
                        type="time"
                        value={bookForm.timeTo}
                        onChange={(value) => patchBookForm(setBookForm, "timeTo", value)}
                      />
                      <InputField
                        label="Subtotal"
                        value={bookForm.subtotal}
                        onChange={(value) => patchBookForm(setBookForm, "subtotal", value)}
                      />
                      <InputField
                        label="GST"
                        value={bookForm.gst}
                        onChange={(value) => patchBookForm(setBookForm, "gst", value)}
                      />
                      <InputField
                        label="Total"
                        value={bookForm.total}
                        onChange={(value) => patchBookForm(setBookForm, "total", value)}
                      />
                    </div>
                    <TextAreaField
                      label="Event address"
                      value={bookForm.eventAddress}
                      onChange={(value) => patchBookForm(setBookForm, "eventAddress", value)}
                    />
                    <TextAreaField
                      label="Special note"
                      value={bookForm.specialNote}
                      onChange={(value) => patchBookForm(setBookForm, "specialNote", value)}
                    />
                  </>
                ) : null}

                {bookType === "mentor_video" || bookType === "mentor_chat" || bookType === "mentor_call" ? (
                  <>
                    <SelectField
                      label="Mentor"
                      value={bookForm.mentorId}
                      onChange={(value) => patchBookForm(setBookForm, "mentorId", value)}
                      options={[
                        { value: "", label: "Select mentor" },
                        ...mentors.map((mentor) => ({
                          value: mentor.id,
                          label: `${mentor.name}${mentor.phone ? ` | ${mentor.phone}` : ""}`,
                        })),
                      ]}
                    />
                    <div style={fieldGrid}>
                      <InputField
                        label="Date"
                        type="date"
                        value={bookForm.dateFrom}
                        onChange={(value) => patchBookForm(setBookForm, "dateFrom", value)}
                      />
                      <InputField
                        label="From time"
                        type="time"
                        value={bookForm.timeFrom}
                        onChange={(value) => patchBookForm(setBookForm, "timeFrom", value)}
                      />
                      <InputField
                        label="To time"
                        type="time"
                        value={bookForm.timeTo}
                        onChange={(value) => patchBookForm(setBookForm, "timeTo", value)}
                      />
                      <InputField
                        label="Amount"
                        value={bookForm.amount}
                        onChange={(value) => patchBookForm(setBookForm, "amount", value)}
                      />
                    </div>
                    <TextAreaField
                      label="Channel / note"
                      value={bookForm.specialNote}
                      onChange={(value) => patchBookForm(setBookForm, "specialNote", value)}
                    />
                  </>
                ) : null}

                {bookType === "song_request" || bookType === "announcement" ? (
                  <>
                    <div style={fieldGrid}>
                      {bookType === "song_request" ? (
                        <>
                          <InputField
                            label="Track name"
                            value={bookForm.trackName}
                            onChange={(value) => patchBookForm(setBookForm, "trackName", value)}
                          />
                          <InputField
                            label="Artist name"
                            value={bookForm.artistName}
                            onChange={(value) => patchBookForm(setBookForm, "artistName", value)}
                          />
                        </>
                      ) : null}
                      <InputField
                        label="Occasion"
                        value={bookForm.occasion}
                        onChange={(value) => patchBookForm(setBookForm, "occasion", value)}
                      />
                      <InputField
                        label="Venue name"
                        value={bookForm.venueName}
                        onChange={(value) => patchBookForm(setBookForm, "venueName", value)}
                      />
                      <InputField
                        label="City"
                        value={bookForm.city}
                        onChange={(value) => patchBookForm(setBookForm, "city", value)}
                      />
                      <InputField
                        label="Date"
                        type="date"
                        value={bookForm.dateFrom}
                        onChange={(value) => patchBookForm(setBookForm, "dateFrom", value)}
                      />
                      <InputField
                        label="Time from"
                        type="time"
                        value={bookForm.timeFrom}
                        onChange={(value) => patchBookForm(setBookForm, "timeFrom", value)}
                      />
                      <InputField
                        label="Time to"
                        type="time"
                        value={bookForm.timeTo}
                        onChange={(value) => patchBookForm(setBookForm, "timeTo", value)}
                      />
                      <InputField
                        label="Amount"
                        value={bookForm.amount}
                        onChange={(value) => patchBookForm(setBookForm, "amount", value)}
                      />
                    </div>
                    <TextAreaField
                      label="Address"
                      value={bookForm.address}
                      onChange={(value) => patchBookForm(setBookForm, "address", value)}
                    />
                    <TextAreaField
                      label={bookType === "announcement" ? "Announcement content" : "Note"}
                      value={bookForm.note}
                      onChange={(value) => patchBookForm(setBookForm, "note", value)}
                    />
                  </>
                ) : null}

                {bookType === "event_booking" ? (
                  <>
                    <SelectField
                      label="Event"
                      value={bookForm.eventId}
                      onChange={(value) =>
                        setBookForm((prev) => ({
                          ...prev,
                          eventId: value,
                          ticketId: "",
                        }))
                      }
                      options={[
                        { value: "", label: "Select event" },
                        ...events.map((event) => ({
                          value: event.id,
                          label: `${event.name}${event.date ? ` | ${event.date}` : ""}`,
                        })),
                      ]}
                    />
                    <SelectField
                      label="Ticket"
                      value={bookForm.ticketId}
                      onChange={(value) => patchBookForm(setBookForm, "ticketId", value)}
                      options={[
                        { value: "", label: "Select ticket" },
                        ...(selectedEvent?.tickets || []).map((ticket) => ({
                          value: ticket.id,
                          label: `${ticket.name} | Rs ${ticket.price.toLocaleString("en-IN")}`,
                        })),
                      ]}
                    />
                    <div style={fieldGrid}>
                      <InputField
                        label="Quantity"
                        value={bookForm.quantity}
                        onChange={(value) => patchBookForm(setBookForm, "quantity", value)}
                      />
                      <InputField
                        label="Contact name"
                        value={bookForm.contactName}
                        onChange={(value) => patchBookForm(setBookForm, "contactName", value)}
                      />
                      <InputField
                        label="Contact phone"
                        value={bookForm.contactPhone}
                        onChange={(value) => patchBookForm(setBookForm, "contactPhone", digits(value))}
                      />
                      <InputField
                        label="Contact email"
                        value={bookForm.contactEmail}
                        onChange={(value) => patchBookForm(setBookForm, "contactEmail", value)}
                      />
                    </div>
                    {selectedEvent ? (
                      <div style={eventPreview}>
                        <div style={previewTitle}>{selectedEvent.name}</div>
                        <div style={previewCopy}>
                          {selectedEvent.date || "Date TBA"} {selectedEvent.time ? `| ${selectedEvent.time}` : ""}
                        </div>
                        <div style={previewCopy}>{selectedEvent.venueName || "Venue not set"}</div>
                      </div>
                    ) : null}
                  </>
                ) : null}

                <div style={buttonRow}>
                  <button
                    type="button"
                    style={primaryBtn}
                    onClick={submitBook}
                    disabled={busyKey === "book"}
                  >
                    <FiSend />
                    {busyKey === "book" ? "Saving..." : `Save ${labelForBook(bookType)}`}
                  </button>
                </div>
              </>
            )}
          </section>
        </div>

        {popup.open ? (
          <div style={popupOverlay}>
            <div style={{ ...popupCard, ...(popup.type === "err" ? popupErr : popupOk) }}>
              <div style={popupTitle}>{popup.title}</div>
              <div style={popupMessage}>{popup.message}</div>
              <button type="button" style={popupBtn} onClick={closePopup}>
                Close
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </PermissionGuard>
  );
}

function TypeSelector({ options, value, onChange }) {
  return (
    <div style={typeGrid}>
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            style={{
              ...typeBtn,
              ...(active ? typeBtnActive : null),
            }}
          >
            {option.icon}
            <span>{option.label}</span>
          </button>
        );
      })}
    </div>
  );
}

function InputField({ label, value, onChange, placeholder = "", type = "text" }) {
  return (
    <label style={labelStyle}>
      {label}
      <input
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        style={input}
      />
    </label>
  );
}

function SelectField({ label, value, onChange, options }) {
  return (
    <label style={labelStyle}>
      {label}
      <select value={value} onChange={(event) => onChange(event.target.value)} style={input}>
        {options.map((option) => (
          <option key={`${label}_${option.value}`} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function TextAreaField({ label, value, onChange, placeholder = "" }) {
  return (
    <label style={labelStyle}>
      {label}
      <textarea
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        style={textarea}
      />
    </label>
  );
}

function InfoPill({ label, value }) {
  return (
    <div className="aa-pro-metric" style={heroPill}>
      <div style={heroPillLabel}>{label}</div>
      <div style={heroPillValue}>{value}</div>
    </div>
  );
}

function patchCreateForm(setter, key, value) {
  setter((prev) => ({ ...prev, [key]: value }));
}

function patchBookForm(setter, key, value) {
  setter((prev) => ({ ...prev, [key]: value }));
}

async function resolveAsset({ file, directUrl, folder, filenamePrefix }) {
  const cleanUrl = cleanText(directUrl);
  if (file) {
    const ext = extensionFromName(file.name || file.type || "file");
    const storagePath = `${folder}/${filenamePrefix}_${Date.now()}.${ext}`;
    const storageRef = ref(storage, storagePath);
    await uploadBytes(storageRef, file);
    const url = await getDownloadURL(storageRef);
    return { url, storagePath };
  }
  return { url: cleanUrl, storagePath: "" };
}

function normalizeUser(id, data = {}) {
  const phone = digits(data.phone || data.mobile || id || "");
  return {
    id,
    uid: String(data.uid || phone || id || ""),
    phone,
    name: cleanText(data.name || data.fullName || data.displayName) || "User",
    username: cleanText(data.username || data.userName),
    city: cleanText(data.city),
    email: cleanText(data.email),
    bio: cleanText(data.bio),
    imageUrl: cleanText(
      data.profileImage ||
        data.profile_image_url ||
        data.imageUrl ||
        data.avatar ||
        data.profilePic ||
        data.photoURL
    ),
  };
}

function buildBrandAssetUrl() {
  if (typeof window === "undefined") return "";
  return new URL("/brand/apnaartist-profile.png", window.location.origin).toString();
}

function firstShowDate(shows = []) {
  const first = Array.isArray(shows) ? shows[0] : null;
  return first?.date || "";
}

function firstShowTime(shows = []) {
  const first = Array.isArray(shows) ? shows[0] : null;
  return Array.isArray(first?.times) ? first.times[0] || "" : "";
}

function cleanText(value) {
  return String(value || "").trim();
}

function digits(value) {
  return String(value || "").replace(/\D/g, "");
}

function splitLines(value) {
  return Array.from(
    new Set(
      String(value || "")
        .split(/[,\n]/g)
        .map((item) => item.trim())
        .filter(Boolean)
    )
  );
}

function sanitizeUsername(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 30);
}

function isOfficialProfile(profile = {}) {
  const username = cleanText(profile.username).toLowerCase();
  const name = cleanText(profile.name).toLowerCase();
  return username === OFFICIAL_USERNAME || name.includes("apnaartist");
}

function toNumber(value) {
  const next = Number(value || 0);
  return Number.isFinite(next) ? next : 0;
}

function withTimeout(promise, timeoutMs, message) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(message)), timeoutMs);
    Promise.resolve(promise)
      .then((value) => {
        clearTimeout(timer);
        resolve(value);
      })
      .catch((error) => {
        clearTimeout(timer);
        reject(error);
      });
  });
}

function parseBackgroundColor(value) {
  const text = cleanText(value);
  if (!text) return null;
  if (/^#([0-9a-f]{6}|[0-9a-f]{8})$/i.test(text)) {
    const hex = text.replace("#", "");
    return parseInt(hex.length === 6 ? `ff${hex}` : hex, 16);
  }
  const numeric = Number(text);
  return Number.isFinite(numeric) ? numeric : null;
}

function extensionFromName(name) {
  const raw = String(name || "");
  const direct = raw.split(".").pop();
  return direct && direct !== raw ? direct.replace(/[^a-z0-9]/gi, "").toLowerCase() || "bin" : "bin";
}

function inferMediaType(url) {
  const target = String(url || "").toLowerCase();
  if (/\.(png|jpe?g|gif|webp|avif|svg)(\?|$)/i.test(target)) return "image";
  if (/\.(mp4|mov|webm|ogg|m4v)(\?|$)/i.test(target)) return "video";
  return "file";
}

function labelForCreate(value) {
  return CREATE_OPTIONS.find((option) => option.value === value)?.label || "Create";
}

function labelForBook(value) {
  return BOOK_OPTIONS.find((option) => option.value === value)?.label || "Booking";
}

function initials(value = "") {
  return String(value || "A")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");
}

function resolveAvatar(value) {
  const text = cleanText(value);
  return text || "";
}

const page = {
  minHeight: "auto",
  background: COLORS.page,
  padding: 18,
  fontFamily: "Inter, system-ui, sans-serif",
};

const hero = {
  borderRadius: 8,
  padding: 28,
  background: "linear-gradient(135deg, rgba(9, 13, 29, 0.98), rgba(31, 19, 38, 0.96)), linear-gradient(90deg, rgba(242,12,83,0.24), rgba(251,108,51,0.18))",
  color: "#fff",
  display: "grid",
  gap: 16,
  justifyItems: "center",
  textAlign: "center",
  boxShadow: "0 22px 46px rgba(15,23,42,0.18)",
};

const heroHeading = { display: "grid", justifyItems: "center", width: "100%" };
const heroTitle = { margin: 0, fontSize: 42, lineHeight: 1.05, fontWeight: 900 };
const heroPill = {
  borderRadius: 8,
  border: "1px solid rgba(255,255,255,0.34)",
  background: "rgba(255,255,255,0.14)",
  padding: "12px 14px",
};
const heroPillLabel = { fontSize: 11, fontWeight: 800, opacity: 0.86, textTransform: "uppercase", letterSpacing: "0.08em" };
const heroPillValue = { marginTop: 4, fontSize: 13, fontWeight: 800 };

const layoutGrid = {
  marginTop: 16,
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr)",
  gap: 18,
  alignItems: "start",
};

const panelCard = {
  borderRadius: 8,
  border: `1px solid ${COLORS.border}`,
  background: "linear-gradient(180deg, #ffffff, #f8fafc)",
  padding: 20,
  boxShadow: "0 18px 36px rgba(15,23,42,0.08)",
  display: "grid",
  gap: 12,
};

const workspaceCard = {
  borderRadius: 8,
  border: `1px solid ${COLORS.border}`,
  background: COLORS.card,
  padding: 20,
  boxShadow: "0 18px 36px rgba(15,23,42,0.08)",
  display: "grid",
  gap: 14,
};

const sectionHeader = { display: "flex", justifyContent: "space-between", gap: 12, alignItems: "start", flexWrap: "wrap" };
const sectionTitle = { fontSize: 20, fontWeight: 900, color: COLORS.text };
const sectionCopy = { marginTop: 4, color: COLORS.subtext, lineHeight: 1.5, fontSize: 13 };

const label = {
  display: "grid",
  gap: 6,
  color: COLORS.text,
  fontWeight: 800,
  fontSize: 13,
};

const labelStyle = label;

const input = {
  width: "100%",
  minHeight: 44,
  borderRadius: 8,
  border: `1px solid ${COLORS.border}`,
  padding: "10px 12px",
  fontSize: 14,
  color: COLORS.text,
  background: "#fff",
};

const textarea = {
  width: "100%",
  minHeight: 108,
  borderRadius: 8,
  border: `1px solid ${COLORS.border}`,
  padding: "12px 14px",
  fontSize: 14,
  color: COLORS.text,
  resize: "vertical",
  background: "#fff",
};

const fieldGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
  gap: 12,
};

const buttonRow = { display: "flex", gap: 10, flexWrap: "wrap", marginTop: 4 };
const inlineStatus = {
  marginTop: 10,
  borderRadius: 8,
  padding: "10px 12px",
  fontSize: 13,
  fontWeight: 700,
  border: "1px solid transparent",
};
const inlineStatusProgress = { background: "#eff6ff", borderColor: "#bfdbfe", color: "#1d4ed8" };
const inlineStatusOk = { background: "#ecfdf5", borderColor: "#86efac", color: "#166534" };
const inlineStatusErr = { background: "#fff1f2", borderColor: "#fca5a5", color: "#b91c1c" };

const primaryBtn = {
  height: 44,
  borderRadius: 8,
  border: "none",
  padding: "0 18px",
  background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
  color: "#fff",
  fontWeight: 900,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  boxShadow: "0 16px 28px rgba(242,12,83,0.2)",
};

const ghostBtn = {
  height: 40,
  borderRadius: 8,
  border: `1px solid ${COLORS.border}`,
  padding: "0 14px",
  background: "#fff",
  color: COLORS.text,
  fontWeight: 800,
  cursor: "pointer",
};

const profilePreview = {
  marginTop: 2,
  padding: 14,
  borderRadius: 8,
  border: `1px solid ${COLORS.border}`,
  background: "linear-gradient(135deg, #fff7fb, #fff4ec)",
  display: "flex",
  gap: 12,
  alignItems: "center",
};

const avatarWrap = {
  width: 68,
  height: 68,
  borderRadius: 8,
  overflow: "hidden",
  display: "grid",
  placeItems: "center",
  background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.secondary})`,
  color: "#fff",
  fontWeight: 900,
  flexShrink: 0,
};

const avatarImage = { width: "100%", height: "100%", objectFit: "cover" };
const previewTitle = { fontWeight: 900, color: COLORS.text };
const previewCopy = { marginTop: 4, color: COLORS.subtext, fontSize: 13 };

const tabRow = { display: "inline-flex", gap: 6, flexWrap: "wrap", width: "fit-content", padding: 4, border: `1px solid ${COLORS.border}`, borderRadius: 8, background: "#f8fafc" };
const tabBtn = {
  height: 42,
  borderRadius: 8,
  border: `1px solid ${COLORS.border}`,
  padding: "0 16px",
  background: "#fff",
  color: COLORS.text,
  fontWeight: 900,
  cursor: "pointer",
};
const tabBtnActive = {
  borderColor: "transparent",
  color: "#fff",
  background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
  boxShadow: "0 12px 24px rgba(242,12,83,0.18)",
};

const typeGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
  gap: 10,
};

const typeBtn = {
  minHeight: 70,
  borderRadius: 8,
  border: `1px solid ${COLORS.border}`,
  background: "#fff",
  color: COLORS.text,
  padding: "12px 14px",
  display: "flex",
  alignItems: "center",
  gap: 10,
  fontWeight: 900,
  cursor: "pointer",
  justifyContent: "center",
};

const typeBtnActive = {
  borderColor: "transparent",
  color: "#fff",
  background: "linear-gradient(135deg, #f20c53, #fb6c33)",
  boxShadow: "0 16px 28px rgba(251,108,51,0.18)",
};

const checkLine = { display: "flex", gap: 8, alignItems: "center", color: COLORS.subtext, fontSize: 13 };

const eventPreview = {
  borderRadius: 8,
  border: `1px solid ${COLORS.border}`,
  background: "#f8fafc",
  padding: 14,
};

const popupOverlay = {
  position: "fixed",
  inset: 0,
  background: "rgba(15,23,42,0.35)",
  display: "grid",
  placeItems: "center",
  zIndex: 9999,
  padding: 16,
};

const popupCard = {
  width: "min(420px, 100%)",
  borderRadius: 20,
  padding: 22,
  boxShadow: "0 28px 54px rgba(15,23,42,0.28)",
  textAlign: "center",
};

const popupOk = { background: COLORS.okBg, color: COLORS.ok, border: "1px solid #86efac" };
const popupErr = { background: COLORS.errBg, color: COLORS.err, border: "1px solid #fca5a5" };
const popupTitle = { fontSize: 20, fontWeight: 900 };
const popupMessage = { marginTop: 8, lineHeight: 1.55 };
const popupBtn = {
  marginTop: 16,
  height: 40,
  borderRadius: 999,
  border: "none",
  padding: "0 16px",
  background: "rgba(15,23,42,0.9)",
  color: "#fff",
  fontWeight: 800,
  cursor: "pointer",
};
